// Utilities for resolving values from data context with pipeline operations

export function getValueByPath(dataContext, path) {
    if (!path || typeof path !== 'string') return undefined;
    const roots = ['apiRow', 'normalized', 'rows', 'plotConfig', 'selectedData'];

    const traverse = (current, dotPath) => {
        const parts = String(dotPath).split('.').filter(Boolean);
        for (let i = 0; i < parts.length && current != null; i++) {
            const key = parts[i];
            if (key === '0') {
                current = Array.isArray(current) ? current[0] : undefined;
            } else if (current != null && Object.prototype.hasOwnProperty.call(current, key)) {
                current = current[key];
            } else {
                current = undefined;
            }
        }
        return current;
    };

    const parseJsonOnce = (value) => {
        if (value == null) return undefined;
        if (typeof value === 'object') return value;
        if (typeof value === 'string') {
            try { return JSON.parse(value); } catch (_e) { return undefined; }
        }
        return undefined;
    };

    const pickField = (item, fieldPath) => traverse(item, fieldPath || '');
    const aggregate = (arr, op, fieldPath) => {

        const list = Array.isArray(arr) ? arr : [];
        if (op === 'count') return list.length;

        const values = list.map(v => Number(pickField(v, fieldPath))).filter(n => Number.isFinite(n));
        if (values.length === 0) return 0;
        if (op === 'sum') return values.reduce((a, b) => a + b, 0);
        if (op === 'avg') return values.reduce((a, b) => a + b, 0) / values.length;
        if (op === 'min') return Math.min(...values);
        if (op === 'max') return Math.max(...values);
        return undefined;
    };

    // 🆕 New helper for find()
    const findInArray = (arr, matchers) => {
        if (!Array.isArray(arr) || !Array.isArray(matchers)) return undefined;
        return arr.find(item =>
            matchers.every(({ field, value }) => {
                const fieldVal = pickField(item, field);
                return fieldVal === value;
            })
        );
    };

    const stages = path.split('|').map(s => s.trim()).filter(Boolean);
    if (stages.length === 0) return undefined;

    const first = stages[0];
    const firstSegs = first.split('.').filter(Boolean);
    let candidates;
    if (roots.includes(firstSegs[0])) {
        candidates = [firstSegs[0]];
    } else {
        candidates = roots;
    }

    let current;
    for (const root of candidates) {
        let base = dataContext?.[root];
        let startIdx = roots.includes(firstSegs[0]) ? 1 : 0;
        current = base;
        for (let i = startIdx; i < firstSegs.length && current != null; i++) {
            const key = firstSegs[i];
            if (key === '0') {
                current = Array.isArray(current) ? current[0] : undefined;
            } else if (current != null && Object.prototype.hasOwnProperty.call(current, key)) {
                current = current[key];
            } else {
                current = undefined;
            }
        }
        if (current !== undefined) break;
    }

    for (let si = 1; si < stages.length && current !== undefined; si++) {
        const token = stages[si];
        const opMatch = token.match(/^\s*(json2|json|sum|avg|min|max|count|find)\s*(?:\(([^)]*)\))?\s*$/);
        if (opMatch) {
            const op = opMatch[1];
            const arg = (opMatch[2] || '').trim();
            if (op === 'json') {
                current = parseJsonOnce(current);
            } else if (op === 'json2') {
                current = parseJsonOnce(parseJsonOnce(current));
            } else if (op === 'sum' || op === 'avg' || op === 'min' || op === 'max' || op === 'count') {
                current = aggregate(current, op, arg);
            } else if (op === 'find') {
                try {
                    const matchers = JSON.parse(arg.replace(/(['"])?([a-zA-Z0-9_]+)(['"])?:/g, '"$2":'));
                    current = findInArray(current, matchers);
                } catch (e) {
                    current = undefined;
                }
            }
        } else {
            current = traverse(current, token);
        }
    }
    return current;
}

export function formatValue(raw, format, options = {}) {
    const { prefix = '', suffix = '', maximumFractionDigits = 2 } = options;
    if (raw == null) return '';
    if (!format || format === 'string') return `${prefix}${String(raw)}${suffix}`;
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(n)) return `${prefix}${String(raw)}${suffix}`;
    if (format === 'currency') {
        return `${prefix}${n.toLocaleString(undefined, { maximumFractionDigits })}${suffix}`;
    }
    if (format === 'percent') {
        return `${prefix}${n.toFixed(0)}%${suffix}`;
    }
    return `${prefix}${n.toLocaleString(undefined, { maximumFractionDigits })}${suffix}`;
}

export const formatCompactNumber = (value) => {
    if (typeof value === 'number') {
        if (value >= 1e9) {
            return (value / 1e9).toFixed(1) + 'B';
        } else if (value >= 1e6) {
            return (value / 1e6).toFixed(1) + 'M';
        } else if (value >= 1e3) {
            return (value / 1e3).toFixed(1) + 'k';
        } else {
            return value.toFixed(2);
        }
    }
    return value;
};